import {
  BaseTemplateArgs,
  TemplateResolver,
  Translator,
  translator,
} from '@solid-primitives/i18n';
import {
  ParentProps,
  createResource,
  children,
  splitProps,
  createRoot,
  Component,
  createComputed,
  createMemo,
} from 'solid-js';
import { parse, IDom } from 'html-parse-string';
import { setting } from '@/stores/setting';
import { env } from '@/stores/env';

/** 替换环境变量 */
const I18nENV = {
  __ENV_COCO__: 'coco', // COCO
};

// 兼容老版本
const resolveTemplate: TemplateResolver = (
  string: string,
  args?: BaseTemplateArgs
) => {
  if (args) {
    for (const [key, value] of Object.entries(args)) {
      const pattern = new RegExp(`__${key}__`, 'g');
      string = string.replace(pattern, value as string);
    }
  }
  return string;
};

function isoLang(lang: string) {
  return env.langs[lang]?.[1] ?? lang;
}

interface I18nInstance {
  t: Translator<Record<string, string>>;
  Trans: Component<ParentProps<TransProps>>;
}

const i18nInstanceCache = new Map<string, I18nInstance>();

export function createI18n(
  locales: Record<string, () => Promise<unknown>>,
  key?: string
) {
  const instance = createRoot<I18nInstance>(() => {
    const [dict] = createResource(
      () => isoLang(setting.lang),
      async (lang) => {
        return await loadResource(lang, locales);
      },
      { initialValue: undefined as any }
    );
    const t = translator(dict, resolveTemplate);
    const wrapT: typeof t = (path, args) => {
      return t(path, args) || path;
    };

    return {
      t: wrapT,
      /**
       * JSX插值，如果要插值过程中添加变量，必须保证他是字符串，
       * 然后把所有的插件当作属性传递，参考下面的deposit变量
       * 比如:
       *<Trans i18nKey='trans.mainpage.lottery.balance' deposit={10}>
          Insufficient balance, select another currency, or <a class='text-primary' href={`/time/${now()}`}>__deposit__</a>
        </Trans>
      */
      Trans: (props) => {
        const [, params] = splitProps(props, [
          'options',
          'i18nKey',
          'children',
        ]);

        const translation = () => t(props.i18nKey, params);
        if (typeof props.children === 'string') {
          return translation as unknown as string;
        } else {
          const clds = children(() => props.children);
          // 合并两个相邻的string节点, 解决代码格式化产生的' '空string结点带来的bug
          const filterClds = createMemo(() => {
            const els = [...clds.toArray()];
            for (const [idx, it] of els.entries()) {
              let pre = els[idx - 1];
              if (typeof it === 'string' && typeof pre === 'string') {
                els[idx - 1] = pre += it;
                els.splice(idx, 1);
              }
            }
            return els;
          });

          /** 额外计算, 不影响渲染，并打印错误 */
          createComputed(() => {
            try {
              const trs = translation();
              if (!trs) return;
              const ast = parse(`<0>${trs}</0>`)[0];
              filterClds().map((node, idx) =>
                replaceElements(node as Node, ast.children[idx])
              );
            } catch (err) {
              console.log(props.i18nKey);
              throw err;
            }
          });
          return filterClds as any;
        }
      },
    };
  });
  if (key) {
    i18nInstanceCache.set(key, instance);
  }
  return instance;
}

function replaceElements(child: Node | string, ast: IDom) {
  if (typeof child === 'string') {
    return ast.content;
  } else if (child.nodeType === 1) {
    replaceWithAst(child, ast);
    return child;
  }
}

// 临时创建一个实例，这并不会生效，必须在渲染第一时间时间设置默认的语言
export const defaultT: Translator<Record<string, string>> = (path, args) => {
  const instance = i18nInstanceCache.get('root');
  return instance ? instance.t(path, args) : path;
};

async function loadResource(
  lang: string,
  locales: Record<string, () => Promise<unknown>>
) {
  let res: Record<string, string> = {};
  // TODO: 原则上来说命名空间应该和项目名相同，但是现在这个期间很多国际化没处理好，暂时兼容下
  const key = `${lang}/index.json`;
  const localesArr = Object.entries(locales);
  const match =
    localesArr.find(([k, v]) => k.endsWith(key)) ||
    localesArr.find(([key, v]) => key.endsWith(`en-US/index.json`));
  if (!lang || !match) {
    res = {};
  } else {
    res = (await match[1]()) as Record<string, string>;

    for (const i in res) {
      res[i] = res[i].replace(/__ENV_\w+__/g, (str) => {
        return env[(I18nENV as any)[str] as keyof typeof env] as string;
      });
    }
  }
  return res;
}

/**
 * 以下方法来自于, 作了部分改进
 * https://github.com/mbarzda/solid-i18next/tree/main
 * @returns
 */

function replaceWithAst(node: Node, ast: IDom) {
  if (node.nodeType === 3) {
    node.textContent = ast.content!;
  } else {
    for (let i = 0; i < node.childNodes.length; i++) {
      replaceWithAst(node.childNodes[i], ast.children[i]);
    }
  }
}

/**
 * JSX插值，如果要插值过程中添加变量，必须保证他是字符串，
 * 然后把所有的插件当作属性传递，参考下面的deposit变量
 * 比如:
 *<Trans i18nKey='trans.mainpage.lottery.balance' deposit={10}>
    Insufficient balance, select another currency, or <a class='text-primary' href={`/time/${now()}`}>__deposit__</a>
  </Trans>
*/
type TransProps = ParentProps<{
  i18nKey: string;
  options?: any;
  [key: string]: any;
}>;
